import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const CraftsmanADUDesignBelmont = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Craftsman ADU Design & Construction in Belmont",
    "description": "Custom Craftsman ADUs in Belmont. Expert design & construction by UDA ADU. Get your free consultation today.",
    "publisher": {
      "@type": "Organization",
      "name": "UDA ADU",
      "url": "https://www.udaadu.com"
    },
    "url": "https://www.udaadu.com/craftsman-adu-design-belmont"
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/craftsman-adu-design-belmont" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData)
      }} />
      
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <section className="text-center py-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-white">
            Craftsman ADU Design & Construction in Belmont
          </h1>
          <p className="text-xl md:text-2xl leading-relaxed max-w-4xl mx-auto text-gray-300 mb-8">
            Build exceptional Craftsman ADUs in Belmont that celebrate the Peninsula's hillside character with authentic handcrafted details and natural materials that honor the landscape.
          </p>
          <Button onClick={openCalendlyModal} className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-4 px-8 rounded-xl shadow-lg transition-colors text-lg">
            Get Free Consultation
          </Button>
        </section>

        {/* Why Choose Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Why Choose Craftsman ADUs in Belmont?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Hillside Craftsmanship</h3>
              <p className="text-gray-300">Craftsman design naturally complements Belmont's rolling hills and mature trees, creating ADUs that feel organically integrated with the Peninsula landscape.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Peninsula Elegance</h3>
              <p className="text-gray-300">Premium materials and artisan details that reflect Belmont's sophisticated Peninsula character and commitment to quality residential living.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Natural Harmony</h3>
              <p className="text-gray-300">Craftsman philosophy of connecting with nature aligns perfectly with Belmont's tree-lined streets and hillside setting.</p>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Craftsman ADU Features & Design Elements
          </h2>
          <div className="bg-white rounded-xl p-8 max-w-4xl mx-auto">
            <ul className="space-y-4 text-gray-800">
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Traditional timber construction adapted for hillside sites</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Custom built-ins maximizing space and functionality</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Hillside porches with panoramic Peninsula views</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Natural stone retaining walls and landscape integration</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Sustainable wood finishes from responsibly sourced materials</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Large windows positioned to capture natural light and hillside views</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Local Considerations */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Belmont-Specific Craftsman ADU Considerations
          </h2>
          <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8 max-w-4xl mx-auto">
            <p className="text-gray-300 mb-6">
              Building Craftsman ADUs in Belmont requires expertise in hillside construction and Peninsula development standards:
            </p>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Belmont's hillside development guidelines and slope regulations</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Design review requirements for Peninsula neighborhood compatibility</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>San Mateo County building codes for hillside construction</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Tree preservation and mature canopy protection requirements</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Peninsula fire safety and defensible space standards</span>
              </li>
            </ul>
            <p className="text-gray-300 mt-6">
              Our hillside construction expertise ensures your Craftsman ADU harmonizes with Belmont's natural beauty while meeting all Peninsula regulatory requirements.
            </p>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            What to Expect: Craftsman ADU Design Process
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Site-Sensitive Design</h3>
              <p className="text-gray-300">We carefully analyze your Belmont hillside property to create Craftsman designs that work with natural topography and preserve mature trees.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Peninsula Permitting</h3>
              <p className="text-gray-300">Our team handles complex Belmont permitting with expertise in hillside development and Peninsula architectural review processes.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Expert Construction</h3>
              <p className="text-gray-300">Skilled craftsmen execute traditional techniques adapted for hillside sites, creating authentic Craftsman character with lasting durability.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Elegant Completion</h3>
              <p className="text-gray-300">From design through final details, we deliver a Craftsman ADU that enhances your Belmont property's Peninsula character and value.</p>
            </div>
          </div>
        </section>

        {/* Cost Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Craftsman ADU Cost in Belmont
          </h2>
          <div className="text-center">
            <Link to="/adu-calculator">
              <Button className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-6 px-12 rounded-xl shadow-lg text-xl">
                Use Our ADU Cost Calculator
              </Button>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
            Ready to Build Your Craftsman ADU in Belmont?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Create a handcrafted ADU that celebrates Belmont's hillside beauty and Peninsula sophistication with authentic Craftsman elegance.
          </p>
          <Button onClick={openCalendlyModal} className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-4 px-8 rounded-xl shadow-lg transition-colors text-lg">
            Book Free Consultation
          </Button>
        </section>

        {/* Internal Links */}
        <section className="py-16">
          <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
            <h3 className="text-2xl font-light text-white mb-6">Explore More ADU Options in Belmont:</h3>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <Link to="/belmont-adu" className="text-[#FF7A00] hover:underline">
                General ADU Services in Belmont
              </Link>
              <Link to="/adu-cost-belmont" className="text-[#FF7A00] hover:underline">
                ADU Cost in Belmont
              </Link>
              <Link to="/adu-regulations-belmont" className="text-[#FF7A00] hover:underline">
                ADU Regulations in Belmont
              </Link>
            </div>
            
            <h3 className="text-2xl font-light text-white mb-6">Other Craftsman ADU Locations:</h3>
            <div className="grid md:grid-cols-4 gap-4">
              <Link to="/craftsman-adu-design-san-mateo" className="text-[#FF7A00] hover:underline">
                Craftsman ADU San Mateo
              </Link>
              <Link to="/craftsman-adu-design-san-carlos" className="text-[#FF7A00] hover:underline">
                Craftsman ADU San Carlos
              </Link>
              <Link to="/craftsman-adu-design-burlingame" className="text-[#FF7A00] hover:underline">
                Craftsman ADU Burlingame
              </Link>
              <Link to="/craftsman-adu-design-redwood-city" className="text-[#FF7A00] hover:underline">
                Craftsman ADU Redwood City
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingADUWizard />
      <FloatingReserveButton />
    </div>
  );
};

export default CraftsmanADUDesignBelmont;